import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ErrorBoundary, useLoaderToast, useMutation, useQuery, useQueryClient } from 'shared-ui';

import {
  drainDodQueue,
  fetchDodQueueDetail,
  fetchDodQueues,
  fetchDodTimeseries,
  pauseDodQueue,
  resumeDodQueue,
} from 'api/dod/dod.v2';
import {
  DOD_OVERVIEW_METRICS,
  DOD_PAGE_SIZE_DEFAULT,
  DodQueueSort,
  DodQueueStatus,
  DodSortOrder,
} from 'api/dod/dod.v2.types';
import { Functionality } from 'api/userPermissions/userPermissions.enum';
import { QueryKeyEnum } from 'core/global.enum';
import { useLocale } from 'hooks/useLocale/useLocale';
import { usePermissions } from 'hooks/usePermissions/usePermissions';
import { AppMessages } from 'i18n/messages';
import { AppRoute } from 'routing/AppRoute.enum';
import { useDodFilters } from '../hooks/useDodFilters/useDodFilters';
import { useDodOutlet } from '../hooks/useDodOutlet/useDodOutlet';
import { sumQueueTotals, toRcpOptions, toTkdOptions } from '../overviewTab/dodFormat';

import { QueueDrawer } from './QueueDrawer';
import { QueuesTab } from './QueuesTab';
import { QueueAction, QueueRowKey, QueueTableRow, queueRowId } from './QueuesTab.types';

const SEARCH_DEBOUNCE_MS = 300;
const DETAIL_PAGE_SIZE = 5;
const DEPTH_WINDOW_MS = 60 * 60 * 1000;
const DEPTH_STEP_MS = 60 * 1000;

const QUEUE_PARAM = 'queue';

const isQueueStatus = (value: string): value is DodQueueStatus =>
  value === 'idle' || value === 'draining' || value === 'paused';

/** `<rcpId>:<nlk>`; the RCP id is a UUID, so the last colon separates the two. */
export const parseQueueParam = (raw: string | null): QueueRowKey | null => {
  if (!raw) {
    return null;
  }

  const separator = raw.lastIndexOf(':');
  const rcpId = separator > 0 ? raw.slice(0, separator) : '';
  const nlk = Number(raw.slice(separator + 1));

  return rcpId && Number.isInteger(nlk) && nlk > 0 ? { rcpId, nlk } : null;
};

export const toQueueParam = (row: QueueRowKey): string => `${row.rcpId}:${row.nlk}`;

export const QueuesTabContainer = (): JSX.Element => {
  const { formatMessage } = useLocale();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { perms } = usePermissions();
  const { refreshToken, setToolbarOptions, setDetails } = useDodOutlet();
  const filters = useDodFilters();
  const { filters: selected, refreshMs, queryString } = filters;
  const [searchParams, setSearchParams] = useSearchParams();

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DOD_PAGE_SIZE_DEFAULT);
  const [detailPage, setDetailPage] = useState(1);
  const [sort, setSort] = useState<DodQueueSort>('depth');
  const [order, setOrder] = useState<DodSortOrder>('desc');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [isBacklogOnly, setIsBacklogOnly] = useState(false);
  const [pendingAction, setPendingAction] = useState<QueueAction | null>(null);

  /**
   * The monitor has no Functionality of its own; the backend borrows UZYTKOWNICY for the DOD
   * routes, so the write actions follow the same permission.
   */
  const canModify = !!perms?.[Functionality.UZYTKOWNICY]?.edit;

  useEffect(() => {
    const timeout = setTimeout(() => {
      setSearch(searchInput.trim());
      // A narrower result set has fewer pages; staying on page 4 of 2 would show an empty table.
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);

    return () => clearTimeout(timeout);
  }, [searchInput]);

  /**
   * The drawer is driven by the URL (`?queue=<rcpId>:<nlk>`), so a deep link opens it on first
   * paint. It deliberately has its own key: `rcp` and `nlk` are the toolbar filters, and writing
   * the selection there narrowed the table to one server every time a row was clicked.
   */
  const selectedRow = useMemo<QueueRowKey | null>(() => parseQueueParam(searchParams.get(QUEUE_PARAM)), [searchParams]);

  const statusFilter = isQueueStatus(selected.status) ? selected.status : undefined;

  const {
    data: queues,
    isLoading,
    isError,
  } = useQuery(
    [
      QueryKeyEnum.DOD_QUEUES,
      page,
      pageSize,
      sort,
      order,
      selected.rcp,
      selected.nlk,
      statusFilter,
      search,
      isBacklogOnly,
      refreshToken,
    ],
    fetchDodQueues,
    {
      handleErrorManually: true,
      args: {
        page,
        pageSize,
        sort,
        order,
        rcpId: selected.rcp || undefined,
        // The toolbar's TKD select is a server-side filter here too, not only on the other tabs.
        nlk: selected.nlk ? Number(selected.nlk) : undefined,
        status: statusFilter,
        search: search || undefined,
        minDepth: isBacklogOnly ? 1 : undefined,
      },
      refetchInterval: refreshMs,
      keepPreviousData: true,
    },
  );

  const { data: detail, isLoading: isDetailLoading } = useQuery(
    [QueryKeyEnum.DOD_QUEUE_DETAIL_V2, selectedRow?.rcpId, selectedRow?.nlk, detailPage, refreshToken],
    fetchDodQueueDetail,
    {
      handleErrorManually: true,
      args: {
        rcpId: selectedRow?.rcpId ?? '',
        nlk: selectedRow?.nlk ?? 0,
        page: detailPage,
        pageSize: DETAIL_PAGE_SIZE,
      },
      enabled: !!selectedRow,
      refetchInterval: refreshMs,
    },
  );

  const { data: detailDepth } = useQuery(
    [QueryKeyEnum.DOD_TIMESERIES, 'queueDepth', selectedRow?.rcpId, selectedRow?.nlk, refreshToken],
    fetchDodTimeseries,
    {
      handleErrorManually: true,
      args: {
        metric: DOD_OVERVIEW_METRICS.queueDepth,
        from: Date.now() - DEPTH_WINDOW_MS,
        to: Date.now(),
        step: DEPTH_STEP_MS,
        rcpId: selectedRow?.rcpId,
        nlk: selectedRow ? String(selectedRow.nlk) : undefined,
      },
      enabled: !!selectedRow,
      refetchInterval: refreshMs,
    },
  );

  const invalidate = useCallback((): void => {
    queryClient.invalidateQueries([QueryKeyEnum.DOD_QUEUES]);
    queryClient.invalidateQueries([QueryKeyEnum.DOD_QUEUE_DETAIL_V2]);
    queryClient.invalidateQueries([QueryKeyEnum.DOD_OVERVIEW]);
  }, [queryClient]);

  const { load, update } = useLoaderToast('dod-queue-action');

  const onActionSettled = useCallback(
    (action: QueueAction, isSuccess: boolean): void => {
      setPendingAction(null);
      update(
        formatMessage({
          id: AppMessages[`dod.queues.action.${action}.${isSuccess ? 'success' : 'error'}` as keyof typeof AppMessages],
        }),
        { type: isSuccess ? 'success' : 'error' },
      );

      if (isSuccess) {
        invalidate();
      }
    },
    [update, formatMessage, invalidate],
  );

  const { mutate: mutateDrain } = useMutation(drainDodQueue, {
    onSuccess: () => onActionSettled('drain', true),
    onError: () => onActionSettled('drain', false),
  });

  const { mutate: mutatePause } = useMutation(pauseDodQueue, {
    onSuccess: () => onActionSettled('pause', true),
    onError: () => onActionSettled('pause', false),
  });

  const { mutate: mutateResume } = useMutation(resumeDodQueue, {
    onSuccess: () => onActionSettled('resume', true),
    onError: () => onActionSettled('resume', false),
  });

  const onAction = useCallback(
    (action: QueueAction, row: QueueRowKey): void => {
      if (!canModify) {
        return;
      }

      setPendingAction(action);
      load(formatMessage({ id: AppMessages[`dod.queues.action.${action}.pending` as keyof typeof AppMessages] }));

      if (action === 'drain') {
        mutateDrain(row);
      } else if (action === 'pause') {
        mutatePause(row);
      } else {
        mutateResume(row);
      }
    },
    [canModify, load, formatMessage, mutateDrain, mutatePause, mutateResume],
  );

  /** Selecting a row only touches `queue`; the toolbar filters in the same URL are left alone. */
  const onSelect = useCallback(
    (row: QueueRowKey | null): void => {
      setDetailPage(1);

      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);

          if (row) {
            next.set(QUEUE_PARAM, toQueueParam(row));
          } else {
            next.delete(QUEUE_PARAM);
          }

          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const onBacklogOnlyToggle = useCallback((): void => {
    setIsBacklogOnly((current) => !current);
    setPage(1);
  }, []);

  /** The table owns the asc/desc toggle and reports the direction it wants; this only records it. */
  const onSortChange = useCallback((nextSort: DodQueueSort, nextOrder: DodSortOrder): void => {
    setPage(1);
    setSort(nextSort);
    setOrder(nextOrder);
  }, []);

  const onPageSizeChange = useCallback((nextPageSize: number): void => {
    setPageSize(nextPageSize);
    setPage(1);
  }, []);

  /** Keeps the range, refresh and custom bounds the operator set; only the TKD filter changes. */
  const onShowInHistory = useCallback(
    (row: QueueRowKey): void => {
      const params = new URLSearchParams(queryString);

      params.set('rcp', row.rcpId);
      params.set('nlk', String(row.nlk));

      navigate(`${AppRoute.dodHistory}?${params.toString()}`);
    },
    [navigate, queryString],
  );

  /**
   * Every narrowing is server-side: RCP, status, the text search and `minDepth: 1` for
   * "backlog only" are all query parameters, so `total` counts matches across the fleet rather
   * than rows on this page. All this does is lay the returned page out by RCP server.
   */
  const rows = useMemo<QueueTableRow[]>(
    () => (queues?.items ?? []).map((row) => ({ ...row, id: queueRowId(row) })),
    [queues],
  );

  const totals = useMemo(() => sumQueueTotals(queues?.items ?? []), [queues]);

  /**
   * The toolbar's selects belong to the shell but their contents belong to whichever tab is
   * fetching; this tab has the queue list, so it publishes the RCP, TKD and state vocabularies.
   */
  useEffect(() => {
    const rows = queues?.items ?? [];

    setToolbarOptions({
      rcp: toRcpOptions(rows),
      tkd: toTkdOptions(rows),
      status: (['idle', 'draining', 'paused'] as const).map((value) => ({
        value,
        label: formatMessage({ id: AppMessages[`dod.common.state.${value}`] }),
      })),
    });
  }, [queues, setToolbarOptions, formatMessage]);

  // The drawer is the page's details panel: PageLayout renders it beside the content through the
  // shell (renderDetails), like every other list + details page in the app.
  useEffect(() => {
    setDetails(
      selectedRow ? (
        <QueueDrawer
          selected={selectedRow}
          detail={detail}
          isLoading={isDetailLoading}
          page={detailPage}
          onPageChange={setDetailPage}
          depth={detailDepth?.series[0]?.points ?? []}
          isDepthUnavailable={!detailDepth?.capability}
          canModify={canModify}
          pendingAction={pendingAction}
          onAction={onAction}
          onShowInHistory={onShowInHistory}
          onClose={() => onSelect(null)}
        />
      ) : null,
    );
  }, [
    setDetails,
    selectedRow,
    detail,
    isDetailLoading,
    detailPage,
    detailDepth,
    canModify,
    pendingAction,
    onAction,
    onShowInHistory,
    onSelect,
  ]);

  useEffect(() => () => setDetails(null), [setDetails]);

  return (
    <ErrorBoundary>
      <QueuesTab
        rows={rows}
        totals={totals}
        isLoading={isLoading}
        isError={isError}
        total={queues?.total ?? 0}
        page={page}
        pageSize={pageSize}
        onPageChange={setPage}
        onPageSizeChange={onPageSizeChange}
        sort={sort}
        order={order}
        onSortChange={onSortChange}
        search={searchInput}
        onSearchChange={setSearchInput}
        isBacklogOnly={isBacklogOnly}
        onBacklogOnlyToggle={onBacklogOnlyToggle}
        selected={selectedRow}
        onSelect={onSelect}
      />
    </ErrorBoundary>
  );
};
